/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';

import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getIsBot } from '../../../state/ducks/App/App-Selectors';
import { getUserType } from '../../../state/ducks/App/ducks/Common/Common-Selectors';
import useGetAccessTokenSafely from '../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../gql';
import findPersonalizedContentSF from '../../gql/queries/findPersonalizedContentSF';
import useBrowserUUID from '../useBrowserUUID';
import { getIsAuthenticatedStatus } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportData, getPassportSubscriptionStatus } from '../../../state/ducks/Passport/Passport-Selectors';
import validatePassport from '../validatePassport';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';

/**
 * @description - used to communicate with customer-personalize API for personalized content.
 * @param { string } campaignFeature - salesforce campaign name
 * @param {boolean} isSympathySku -
 * @param {string} partNumber -
 * @param {object} targeting -
 * @param { object } apolloOptions - overrides | additional options to pass to useQuery API
 * @param {boolean} optInHoldoutParticipation - overrides holdout participation for specific UI/Components
 * @returns { object } - { loading, error, data, variables } -> add variables.skip to loading handling and variables.eidError to error handling
 */

const useSalesforcePersonalizedContentQuery = ({
    interactionName,
    targeting,
    apolloOptions = {},
    queryName = 'PersonalizedContent',
    featureFlag = true, // default but negated to skip if statement
    isSessionHasAccount = false, // needed for checking if guest user has account
    optInHoldoutParticipation = false,
    resolveIdentity = false,
}) => {
    const isBot = useSelector(getIsBot);
    const isSalesForceRecsEnabled = useSelector(getFeatureFlag('is-salesforce-recs-enabled'));

    // if ff true negate make false and skip statement and make api call / if false negate and make true to return from call and continue
    if (isBot || !featureFlag || !isSalesForceRecsEnabled) {
        return {
            loading: false, error: false, data: null, variables: { skip: false },
        };
    }

    const query = findPersonalizedContentSF(queryName);
    const brand = useSelector(getBrandName) || '1800flowers';
    const isPersonalizationHoldoutEnabled = optInHoldoutParticipation ? useSelector(getFeatureFlag('is-personalization-holdout-enabled')) : optInHoldoutParticipation;
    const browserUUID = useBrowserUUID();

    const jwt = useGetAccessTokenSafely();
    // skip until jwt is loading
    const skip = Boolean(!jwt || !browserUUID);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    let sessionCustomerType;
    if ((isPassportMember && isAuthenticated) || isPassportAdded) {
        sessionCustomerType = 'P'; // Passport members > Registered users
    } else if (isAuthenticated) {
        sessionCustomerType = 'R'; // Registered user, but not a Passport member
    } else {
        sessionCustomerType = 'G'; // Guest
    }

    // set sessionLoginState according to user login state
    const userType = useSelector(getUserType);
    const userAttributes = [];
    userAttributes.push({ name: 'sessionLoginState', value: userType === 'R' || userType === 'P' ? 'true' : 'false' });
    userAttributes.push({ name: 'sessionCustomerType', value: sessionCustomerType });
    // code block for sending hasAccountCookie key under userAttributes
    if (isSessionHasAccount) userAttributes.push({ name: 'hasAccountCookie', value: 'true' });

    return useQuery(query, {
        variables: (skip) ? { skip } : {
            // use default variables directly
            brand,
            environment: GRAPHQL_ENV,
            isHoldoutEligible: isPersonalizationHoldoutEnabled,
            resolveIdentity,
            user: {
                anonymousId: browserUUID,
            },
            userAttributes,
            targeting: {
                interactionName,
                ...targeting,
            },
        },
        context: { api: 'personalizedContent', jwt },
        fetchPolicy: 'no-cache',
        skip,
        ...apolloOptions,
    });
};

export default useSalesforcePersonalizedContentQuery;
